import { Formik, FormikConfig, FormikProps, FormikValues } from 'formik';
import React from 'react';

import { ActionButtonLayout } from '../ActionButtonLayout';
import MainContentContainer from '../MainContentContainer';
import IsDisabledProvider, { useIsDisabledByParent } from '../IsDisabledProvider';
import { getIntl } from '../../../utils/intl';
import { isPresent } from 'ts-is-present';
import { Action } from '../ActionButtonLayout/types';
import { ButtonVariant } from '../Button';
import TitledSection from '../TitledSection';
import CenteredFormRow from './CenteredFormRow';
import { IconName } from '../Icon';
import { StyleProp, ViewStyle } from 'react-native';
import { ForceStackFieldsContext } from './ForcedStackFieldsContext';

const intl = getIntl('form', {
    save: 'Save',
    cancel: 'Cancel',
});

export type FormikChildren<TValues = any> = (props: FormikProps<TValues>) => React.ReactNode;

export type FormOwnProps<TValue = any> = {
    isDisabled?: boolean;
    title?: string;
    actionContainerSubmit?: boolean;
    onCancel?: () => void;
    children: FormikChildren<TValue>;
    saveText?: string;
    cancelText?: string;
    /** Actions to render if the form is not dirty/not modified */
    nonDirtyActions?: Action[];
    cancelButtonVariant?: ButtonVariant;
    noScroll?: boolean;
    fillContainer?: boolean;
    saveIcon?: IconName;
    cancelIcon?: IconName;
    style?: StyleProp<ViewStyle>;
    forceStackFields?: boolean;
    contentContainerStyle?: StyleProp<ViewStyle>;
    centerTitle?: boolean;
    /**
     * Ability to intercept the form submission to allow for custom logic before the form is validated.
     *
     * If submission should proceed, call the given form submission callback
     */
    onSubmitPress?: OnSubmitPress;
};

export type FormProps<TValue extends FormikValues = FormikValues> = FormOwnProps<TValue> &
    Omit<FormikConfig<TValue>, 'children'>;

/**
 * Intercept the form submission to allow for custom logic before the form is validated.
 */
export type OnSubmitPress = (formikSubmit: () => void) => void;

export function Form<TValue extends FormikValues = FormikValues>({
    actionContainerSubmit,
    title,
    isDisabled,
    children,
    onCancel,
    saveText,
    nonDirtyActions,
    cancelText,
    cancelButtonVariant,
    noScroll,
    fillContainer,
    saveIcon,
    forceStackFields,
    contentContainerStyle,
    centerTitle,
    cancelIcon,
    onSubmitPress,
    ...toPass
}: FormProps<TValue>) {
    const isDisabledByParent = useIsDisabledByParent(isDisabled);
    const renderInFormik = (children: FormikChildren<TValue>) => <Formik {...toPass}>{children}</Formik>;

    const renderWithDisabledProvider = (formikProps: FormikProps<TValue>, children: FormikChildren<TValue>) => (
        <IsDisabledProvider isDisabled={isDisabledByParent || formikProps.isSubmitting}>
            {children(formikProps)}
        </IsDisabledProvider>
    );

    const maybeRenderWithTitle = (formikProps: FormikProps<TValue>, children: FormikChildren<TValue>) => {
        if (!title) {
            return children(formikProps);
        }

        return (
            <CenteredFormRow>
                <TitledSection title={title} centerTitle={centerTitle}>
                    {children(formikProps)}
                </TitledSection>
            </CenteredFormRow>
        );
    };

    const maybeRenderInActionContainer = (formikProps: FormikProps<TValue>, children: FormikChildren<TValue>) => {
        if (!actionContainerSubmit) {
            return children(formikProps);
        }

        return (
            <ActionButtonLayout
                scrollable={!noScroll}
                whiteBackground
                actions={
                    !!nonDirtyActions && !formikProps.dirty
                        ? nonDirtyActions
                        : [
                              onCancel
                                  ? ({
                                        title: cancelText || intl('cancel'),
                                        onPress: onCancel,
                                        icon: cancelIcon || 'XCircle',
                                        isDisabled: formikProps.isSubmitting || isDisabledByParent,
                                        variant: cancelButtonVariant || 'secondary',
                                        testID: 'form-submit-cancel',
                                    } as const)
                                  : null,
                              {
                                  title: saveText || intl('save'),
                                  onPress: () => {
                                      if (onSubmitPress) {
                                          onSubmitPress(() => formikProps.handleSubmit());
                                          return;
                                      } else {
                                          formikProps.handleSubmit();
                                      }
                                  },
                                  isDisabled: formikProps.isSubmitting || isDisabledByParent,
                                  icon: saveIcon || 'CheckCircle',
                                  isPrimary: !onCancel,
                                  testID: 'form-submit-button',
                              } as const,
                          ].filter(isPresent)
                }
            >
                <MainContentContainer
                    flex={fillContainer ? 1 : undefined}
                    style={contentContainerStyle}
                    testID="form-main-content-container"
                >
                    {children(formikProps)}
                </MainContentContainer>
            </ActionButtonLayout>
        );
    };

    return (
        <ForceStackFieldsContext.Provider value={forceStackFields || false}>
            {renderInFormik((formikProps) =>
                renderWithDisabledProvider(formikProps, (formikProps) =>
                    maybeRenderInActionContainer(formikProps, (formikProps) =>
                        maybeRenderWithTitle(formikProps, (formikProps) => children(formikProps)),
                    ),
                ),
            )}
        </ForceStackFieldsContext.Provider>
    );
}
